import { z } from 'zod';
import { IdSchema, InstantSchema, StageSchema } from './common.schema';

/**
 * `GET /board`. The live floor board, sections 5.2 and 8.2.
 *
 * One call rather than three, for the same reason the print payload is one
 * call: this screen refreshes every five seconds on a wall display, and three
 * round trips at that rate is three times the load for a view that is only
 * useful when its parts agree with each other.
 *
 * Live work in particular cannot be derived from `GET /kits`. A kit carries
 * who it is assigned to, not who is timing it right now, and "assigned" and
 * "being worked" are different things on a floor where a manager assigns in
 * the morning and the technician scans in after lunch.
 */

export const StageLoadSchema = z.object({
  stage: StageSchema,
  kits: z.number().int().nonnegative(),
  /**
   * Kits in this stage that are late: the due date has passed, on the lab's
   * calendar. Not kits that are due soon. The request of 7 October 2026, section 6.
   */
  overdue: z.number().int().nonnegative(),
  /** The longest any kit has sat in this stage, in minutes. */
  oldestWaitMinutes: z.number().int().nonnegative(),
});

/**
 * One running task, right now. ONE ROW PER TASK, not per person: since section 2
 * of the request of 20 September 2026 a technician may be timing three cases, and
 * three chips under one name is what a manager looking for a free pair of hands
 * needs to see.
 *
 * `startedAt` rather than an elapsed count, because the client ticks its own
 * clock once per second for the whole page and a server sent duration would be
 * stale before it rendered. Section 10 item 4.
 */
export const LiveWorkSchema = z.object({
  kitId: IdSchema,
  jobOrderNo: z.string(),
  product: z.string(),
  stage: StageSchema,
  userId: IdSchema,
  userName: z.string(),
  startedAt: InstantSchema,
  /**
   * Running longer than the lab's working day, so nobody has stopped it.
   *
   * Section 1.3 of the request of 20 September 2026: the clock runs until Pause
   * or Done, so a forgotten timer is now possible, and this is how the next
   * person to look at the board notices rather than the report a month later.
   * The screens draw it in watch tone and read "Running since yesterday".
   */
  overrunning: z.boolean(),
});

export const AttentionReasonSchema = z.enum(['urgent', 'overdue', 'rework']);

export const AttentionRowSchema = z.object({
  kitId: IdSchema,
  jobOrderNo: z.string(),
  product: z.string(),
  stage: StageSchema,
  reason: AttentionReasonSchema,
  /**
   * Every reason that applies, loudest first: urgent, overdue, rework. `reason`
   * is the first of them, and what the list is ordered by. A row said one and
   * hid the others: the lab's answer of 8 October 2026. Optional, so a screen
   * read against an API that predates it still draws the one.
   */
  reasons: z.array(AttentionReasonSchema).optional(),
  daysLeft: z.number().int(),
  /** Absent without seeIdentity, section 7.10 item 1. */
  patientName: z.string().nullable().optional(),
  assignedToUserName: z.string().nullable(),
});

/**
 * The Logistics column, after Ready on the production line. Section 2 of the
 * request of 13 September 2026.
 *
 * NOT a stage, and kept beside `stages` rather than in it. `stages` is keyed by
 * `StageSchema`, which carries seven values that travel on the wire and are the
 * same values the .NET entities carry; an eighth would land in the three
 * production routes, the flag destinations, the bench rule,
 * the department union, every seeded kit and a migration across the stage
 * history of every case the lab has ever run.
 *
 * And it would be wrong on its own terms. A case in logistics is not being
 * worked by a technician at a bench: its kit is finished and sitting at Ready,
 * and what has moved is the ORDER, from awaiting payment to paid to gone. That
 * is `OrderState` and the shipping status, which already model it exactly.
 *
 * These count ORDERS, where every other figure on this payload counts kits.
 */
export const LogisticsSchema = z.object({
  /** Paid, still in the building: waiting for a courier or for the van. */
  readyToDispatch: z.number().int().nonnegative(),
  /** Collected by a courier, waybill code not typed in yet. */
  awaitingTracking: z.number().int().nonnegative(),
  /** On its way, whichever route. */
  inTransit: z.number().int().nonnegative(),
  /** Held after Ready. It appears here, because the dispatch desk needs to see it. */
  held: z.number().int().nonnegative(),
  /**
   * Always 0 since the request of 7 October 2026, section 6: a case past Ready
   * is finished, and a finished case is never overdue. Kept so that a copy of
   * the app from before that still reads a number.
   */
  overdue: z.number().int().nonnegative(),
});

export const BoardSchema = z.object({
  /** Kits not yet dispatched, which is what "on the line" means. */
  activeKits: z.number().int().nonnegative(),
  stages: z.array(StageLoadSchema),
  /** The eighth card. Orders past Ready and not yet arrived. */
  logistics: LogisticsSchema,
  liveWork: z.array(LiveWorkSchema),
  /** Urgent, overdue or in rework. Most pressing first. */
  attention: z.array(AttentionRowSchema),
});

export type Logistics = z.infer<typeof LogisticsSchema>;
export type StageLoad = z.infer<typeof StageLoadSchema>;
export type LiveWork = z.infer<typeof LiveWorkSchema>;
export type AttentionRow = z.infer<typeof AttentionRowSchema>;
export type Board = z.infer<typeof BoardSchema>;
